import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AlertBox from '../components/AlertBox';
import CancellationModal from '../components/CancellationModal';
import { API_BASE_URL } from '../config';

interface Booking {
  booking_reference: string;
  event_title: string;
  event_start_date: string;
  event_end_date: string;
  event_address: string;
  customer_name: string;
  email: string;
  adults: number;
  kids: number;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  mode: string;
  confirmed: boolean;
  cancelled: boolean;
  cancelled_at: string | null;
  ticket_emailed: boolean;
  admitted: boolean;
  created_at: string;
  refunded: boolean;
  refund_amount: number;
  refunded_at: string | null;
  canCancel?: boolean;
  hoursUntilEvent?: number;
}

const ManageBooking: React.FC = () => {
  const { reference } = useParams<{ reference?: string }>();
  const navigate = useNavigate();

  const [bookingRef, setBookingRef] = useState(reference || '');
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Automatically lookup booking if reference is provided in URL
    if (reference && reference.trim()) {
      lookupBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const lookupBooking = async () => {
    if (!bookingRef.trim()) {
      setError('Please enter a booking reference');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setBooking(null);

      const response = await fetch(
        `${API_BASE_URL}/public-tickets/booking/${bookingRef.trim().toUpperCase()}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Booking not found');
      }

      setBooking(data.booking);
    } catch (err: any) {
      console.error('Error looking up booking:', err);
      setError(err.message || 'Failed to find booking');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCancellationOTP = async (validatedEmail: string) => {
    if (!booking) return;

    const response = await fetch(
      `${API_BASE_URL}/public-tickets/request-cancellation-otp`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_reference: booking.booking_reference,
          email: validatedEmail
        })
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send verification code');
    }
  };

  const handleConfirmCancellation = async (otp: string, validatedEmail: string) => {
    if (!booking) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(
        `${API_BASE_URL}/public-tickets/booking/${booking.booking_reference}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: validatedEmail,
            otp: otp
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel booking');
      }

      let successMessage = 'Booking cancelled successfully';
      if (data.refund) {
        successMessage += `. ${data.refund.message}`;
        setBooking(prev => prev ? { 
          ...prev, 
          cancelled: true, 
          cancelled_at: new Date().toISOString(),
          refunded: true,
          refund_amount: data.refund.amount,
          refunded_at: new Date().toISOString()
        } : null);
      } else {
        setBooking(prev => prev ? { ...prev, cancelled: true, cancelled_at: new Date().toISOString() } : null);
      }
      
      setSuccess(successMessage);
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Your Booking</h1>
        </div>

        {/* Booking Reference Lookup */}
        {!booking && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {error && <AlertBox type="error" message={error} />}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Booking Reference
              </label>
              <input
                type="text"
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value.toUpperCase())}
                placeholder="Enter 6-character code"
                maxLength={6}
                className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase text-center tracking-widest"
              />
            </div>

            <button
              onClick={lookupBooking}
              disabled={loading || !bookingRef.trim()}
              className="w-full bg-gradient-to-r from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white py-3 px-6 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Looking up...' : 'Find Booking'}
            </button>
          </div>
        )}

        {/* Booking Details */}
        {booking && (
          <div className="space-y-6">
            {error && <AlertBox type="error" message={error} />}
            {success && <AlertBox type="success" message={success} />}

            {/* Status Banner */}
            {booking.cancelled && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                <p className="text-red-800 font-semibold text-center">
                  ❌ This booking has been cancelled
                </p>
                {booking.cancelled_at && (
                  <p className="text-red-600 text-sm text-center mt-1">
                    Cancelled on {new Date(booking.cancelled_at).toLocaleString()}
                  </p>
                )}
                {booking.refunded && (
                  <div className="mt-3 pt-3 border-t border-red-300">
                    <p className="text-red-700 text-sm text-center">
                      💳 Refund of ${booking.refund_amount?.toFixed(2)} processed
                    </p>
                    {booking.refunded_at && (
                      <p className="text-red-600 text-xs text-center mt-1">
                        Refunded on {new Date(booking.refunded_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {booking.confirmed && !booking.cancelled && (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                <p className="text-green-800 font-semibold text-center">
                  ✅ Booking Confirmed
                </p>
              </div>
            )}

            {/* Booking Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Booking Details</h2>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Reference</p>
                  <p className="text-lg font-mono font-bold text-primary-600">
                    {booking.booking_reference}
                  </p>
                </div>
              </div>

              {/* Event Information */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{booking.event_title}</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Date:</strong> {new Date(booking.event_start_date).toLocaleString()}</p>
                  <p><strong>Location:</strong> {booking.event_address}</p>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Name:</strong> {booking.customer_name}</p>
                  <p><strong>Email:</strong> {booking.email}</p>
                </div>
              </div>

              {/* Ticket Information */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Tickets</h3>
                <div className="space-y-2 text-gray-600">
                  {booking.adults > 0 && <p><strong>Adults:</strong> {booking.adults} ticket(s)</p>}
                  {booking.kids > 0 && <p><strong>Kids:</strong> {booking.kids} ticket(s)</p>}
                  <p><strong>Payment Method:</strong> {booking.mode === 'stripe' ? 'Credit Card (Stripe)' : booking.mode === 'interac' ? 'Interac E-Transfer' : 'Cash'}</p>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Summary</h3>
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span className="font-semibold">
                    ${booking.original_amount.toFixed(2)}
                  </span>
                </div>
                
                {booking.discount_amount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount:</span>
                    <span className="font-semibold">
                      -${booking.discount_amount.toFixed(2)}
                    </span>
                  </div>
                )}
                
                <div className="pt-2 border-t border-gray-300 flex justify-between text-lg font-bold text-primary-700">
                  <span>Total:</span>
                  <span>
                    ${booking.final_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {!booking.cancelled && (
              <div className="bg-white rounded-lg shadow-md p-6">
                {booking.canCancel ? (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-3 px-6 rounded-lg font-semibold transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Cancel Booking
                  </button>
                ) : (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                    <p className="text-yellow-800 font-semibold text-center">
                      ⚠️ Cancellation Not Available
                    </p>
                    <p className="text-yellow-700 text-sm text-center mt-2">
                      Cancellations must be made at least 24 hours before the event.
                      {booking.hoursUntilEvent !== undefined && (
                        <span className="block mt-1">
                          Event starts in {booking.hoursUntilEvent.toFixed(1)} hours.
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* New Booking Link */}
            <div className="text-center">
              <button
                onClick={() => {
                  setBooking(null);
                  setBookingRef('');
                  setError(null);
                  setSuccess(null);
                }}
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                ← Look up another booking
              </button>
            </div>
          </div>
        )}

        {/* Browse Events Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/events')}
            className="text-primary-600 hover:text-primary-700 font-semibold"
          >
            Browse All Events →
          </button>
        </div>
      </div>

      {/* Cancellation Modal */}
      {booking && (
        <CancellationModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleConfirmCancellation}
          bookingReference={booking.booking_reference}
          email={booking.email}
          eventTitle={booking.event_title}
          onRequestOTP={handleRequestCancellationOTP}
        />
      )}
    </div>
  );
};

export default ManageBooking;
